import {
  EMemberChoicesLabels,
  IMemberChoices,
} from 'affiny-core/modules/api/models/memberchoice/memberchoice.types';
import { IAnswer } from 'affiny-core/modules/api/models/reflist/reflist.types';
import {
  EReflistChapterName,
  IReflistChapter,
  TReflistQuestion,
} from 'affiny-core/modules/api/repositories/reflists/reflists.types';
import {
  IUnanswered,
  TAnswers,
  TAnswersLabels,
  TPartialAnswerLabels,
  TPartialAnswers,
} from 'affiny-core/modules/memberchoices/types';
import { EReflistSubject } from 'affiny-core/modules/reflists/types';
import { IRootState } from 'affiny-core/modules/state';
import { selectAccountId } from 'affiny-core/redux/selectors/accountSelectors';
import {
  getPreviousChapterLabel,
  selectAnswerLabelById,
  selectProfileJobById,
  selectReflists,
} from 'affiny-core/redux/selectors/reflistsSelectors';
import { DeepPartial } from 'affiny-core/types';
import mapValues from 'lodash/mapValues';
import pick from 'lodash/pick';
import { createSelector } from 'reselect';
import { selectHobbies } from './choicesItemsSelectors/hobbiesSelectors';
import { selectInformations } from './choicesItemsSelectors/informationSelectors';
import { selectTastes } from './choicesItemsSelectors/tastesSelectors';
import { selectVision } from './choicesItemsSelectors/visionSelectors';
import { selectYearnings } from './choicesItemsSelectors/yearningsSelectors';

/* ***** *****  Utils  ***** ***** */

const filterLabels = (memberChoices?: TPartialAnswers) =>
  mapValues(memberChoices, values =>
    (values || []).map(({ label }) => label)
  ) as TAnswersLabels;

const selectMemberChoiceAnswersByLabels = (
  state: IRootState,
  labels: EMemberChoicesLabels[],
  accountId?: string,
  subject: EReflistSubject = EReflistSubject.OTHER
): TPartialAnswers | undefined => {
  const membersChoices = accountId
    ? selectMemberChoicesByAccountId(state, accountId)
    : selectMyProfileMemberChoices(state);

  if (!membersChoices) return;

  const filteredMemberChoices = pick<IMemberChoices>(membersChoices, labels);

  return mapValues<DeepPartial<IMemberChoices>, IAnswer[]>(
    filteredMemberChoices,
    (ids, label) =>
      Array.isArray(ids)
        ? ids.map(
            (id: number) =>
              ({
                label: selectAnswerLabelById(state, label, id, subject),
                id,
              } as IAnswer)
          )
        : []
  );
};

export const selectMemberChoiceAnswersBySearchCriteria = (
  state: IRootState,
  labels: EMemberChoicesLabels[]
) => {
  const membersChoices = selectMyProfileMemberChoices(state);
  const filteredMemberChoices = pick<IMemberChoices>(membersChoices, labels);

  return filteredMemberChoices;
};

export const selectMyProfileMemberChoices = (state: IRootState) => {
  const accountId = selectAccountId(state);

  return accountId
    ? selectMemberChoicesByAccountId(state, accountId)
    : undefined;
};

const selectProfileMemberChoicesKeys = createSelector(
  selectMyProfileMemberChoices,
  memberchoices => (memberchoices ? Object.keys(memberchoices) : [])
);

/* ***** *****  PC questions  ***** ***** */

const questionsLabels = [
  // Vision
  EMemberChoicesLabels.sociotestLook,
  EMemberChoicesLabels.sociotestBodyPartAttraction,
  // Informations
  //  Physical
  EMemberChoicesLabels.profileHairStyle,
  EMemberChoicesLabels.profileHairColor,
  EMemberChoicesLabels.profileEyes,
  //  Height
  EMemberChoicesLabels.profileBodyShape,
  EMemberChoicesLabels.profileHeight,
  EMemberChoicesLabels.profileWeight,
  //  Statut
  EMemberChoicesLabels.profileMaritalStatus,
  EMemberChoicesLabels.childNumber,
  //  Fumeur
  EMemberChoicesLabels.profileSmokeHabit,
  //  Alcool
  EMemberChoicesLabels.alcoholHabit,
  // Future
  EMemberChoicesLabels.profileChildrenWish,
  // Tastes
  EMemberChoicesLabels.sociotestMusicStyle,
  EMemberChoicesLabels.sociotestFilm,
  EMemberChoicesLabels.profileLeisure,
  EMemberChoicesLabels.selftestHolidayDestination,
  // Hobbies
  EMemberChoicesLabels.profileHobbies,
  EMemberChoicesLabels.profileSports,
  EMemberChoicesLabels.profileStudies,
  EMemberChoicesLabels.profileIncome,
  EMemberChoicesLabels.profilePet,
  EMemberChoicesLabels.profileFoodHabit,
  // Origin
  EMemberChoicesLabels.profileEthnicity,
  EMemberChoicesLabels.profileNationality,
  // Religion
  EMemberChoicesLabels.profileReligion,
  EMemberChoicesLabels.profileReligionBehaviour,
  // Yearnings
  EMemberChoicesLabels.sociotestLuxuryDefinition,
  EMemberChoicesLabels.sociotestLittleWeakness,
  EMemberChoicesLabels.sociotestLivingIn,
  EMemberChoicesLabels.sociotestQualityTime,
  EMemberChoicesLabels.sociotestStressManagment,
  EMemberChoicesLabels.sociotestMakeMeHappy,
];

export const searchCriteriaQuestionsLabel = [
  // Age
  EMemberChoicesLabels.searchAge,
  //  Height
  EMemberChoicesLabels.searchHeight,
  //  Etudes
  EMemberChoicesLabels.searchStudies,
  EMemberChoicesLabels.searchStudiesWeighting,
  //  Fumeur
  EMemberChoicesLabels.searchSmokeHabit,
  EMemberChoicesLabels.searchSmokeHabitWeighting,
  // Enfants
  EMemberChoicesLabels.childsAtHome,
  EMemberChoicesLabels.childsAtHomeWeighting,
  // Religion
  EMemberChoicesLabels.searchReligion,
  EMemberChoicesLabels.searchReligionWeighting,
  // Origin
  EMemberChoicesLabels.searchEthnicity,
  EMemberChoicesLabels.searchEthnicityWeighting,
];

/* ***** *****  PC Selectors  ***** ***** */

// TODO: gérer des expections si pas authentifié
export const getFirstUnansweredQuestion = createSelector(
  selectProfileMemberChoicesKeys,
  selectReflists,
  (state: IRootState) => state,
  (memberChoicesKeys, reflists, state) => {
    let result: IUnanswered | undefined;
    let unanswered = false;

    Object.keys(reflists).forEach((chapterKey: EReflistChapterName) => {
      const chapter: IReflistChapter = reflists[chapterKey] as IReflistChapter;

      if (chapter) {
        chapter.questions.forEach((multiple, index) => {
          if (!unanswered) {
            unanswered = multiple.some(
              question => !memberChoicesKeys.includes(question.label)
            );

            if (unanswered) {
              result = {
                chapterLabel: chapterKey,
                questionIndex: index,
                previousChapterLabel: getPreviousChapterLabel(
                  state,
                  chapterKey
                ),
              };
            }
          }
        });
      }
    });

    return result;
  }
);

/* ***** *****  Selectors  ***** ***** */

export const selectMemberChoiceByQuestions = (
  state: IRootState,
  questions: TReflistQuestion[]
): IMemberChoices => {
  const memberChoices = selectMyProfileMemberChoices(state);

  if (!memberChoices || !questions) return {};

  return questions.reduce((answers, question) => {
    const label = question.label;

    if (memberChoices[label]) {
      answers[label] = memberChoices[label];
    }

    return answers;
  }, {});
};

export const selectMemberChoicesByAccountId = (
  state: IRootState,
  accountId: string
) => state.memberchoices[accountId]?.values;

export const selectMemberChoiceAnswerLabelsByLabels = (
  state: IRootState,
  labels: EMemberChoicesLabels[],
  accountId?: string
): TPartialAnswerLabels | undefined => {
  const memberChoicesByLabels = selectMemberChoiceAnswersByLabels(
    state,
    labels,
    accountId
  );

  return filterLabels(memberChoicesByLabels);
};

export const selectMemberChoiceAnswerByLabel = (
  state: IRootState,
  label: string,
  payloadAccountId?: string
): string[] | undefined => {
  const accountId = selectAccountId(state) || '';
  const selectedId = payloadAccountId || accountId;
  const membersChoices = selectMemberChoicesByAccountId(state, selectedId);

  return membersChoices ? membersChoices[label] : undefined;
};

export const selectProfileJobByMemberId = (
  state: IRootState,
  accountId?: string,
  subject?: EReflistSubject
) => {
  const labelsId = selectMemberChoiceAnswerByLabel(
    state,
    'profileJob',
    accountId
  );
  const jobId = labelsId && labelsId[0];

  return jobId === null
    ? ''
    : selectProfileJobById(state, Number(jobId), subject);
};

export const selectProfileDisplayVisitedTabData = (
  state: IRootState,
  accountId: string,
  subject: EReflistSubject = EReflistSubject.OTHER
) => {
  const memberAnswers = selectMemberChoiceAnswersByLabels(
    state,
    questionsLabels,
    accountId,
    subject
  ) as TAnswers;

  const myMemberAnswers = selectMemberChoiceAnswersByLabels(
    state,
    questionsLabels,
    accountId,
    EReflistSubject.ME
  ) as TAnswers;

  if (!memberAnswers) return {};

  const memberAnswerLabels = filterLabels(memberAnswers);

  return {
    vision: selectVision(memberAnswerLabels),
    information: selectInformations(memberAnswerLabels, memberAnswers),
    myInformation: selectInformations(memberAnswerLabels, myMemberAnswers),
    tastes: selectTastes(memberAnswerLabels),
    hobbies: selectHobbies(state, memberAnswerLabels),
    yearnings: selectYearnings(memberAnswerLabels),
  };
};
